const RELOAD_TIMESTAMP_KEY = 'tundr.stale-chunk-reload-at' as const;
const RELOAD_COOLDOWN_MS = 10_000;

const STALE_CHUNK_MESSAGE_PATTERNS = [
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
  /Failed to fetch dynamically imported module/i,
  /is not a valid JavaScript MIME type/i,
  /Unable to preload CSS/i,
];

/**
 * Recognises the browser-specific wordings for "the chunk this build asked for is
 * no longer on the CDN", which is what a session open across a deploy runs into.
 */
export function isStaleChunkErrorMessage(message: string | undefined): boolean {
  if (!message) {
    return false;
  }
  return STALE_CHUNK_MESSAGE_PATTERNS.some((pattern) => pattern.test(message));
}

/**
 * Allows one reload per cooldown window so a chunk that stays unreachable after
 * reloading cannot put the tab in a refresh loop.
 */
export function shouldReloadForStaleChunk(
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  now: number,
): boolean {
  const lastReloadAt = Number(storage.getItem(RELOAD_TIMESTAMP_KEY));
  if (lastReloadAt && now - lastReloadAt < RELOAD_COOLDOWN_MS) {
    return false;
  }
  storage.setItem(RELOAD_TIMESTAMP_KEY, String(now));
  return true;
}

export type StaleChunkReloadTarget = {
  addEventListener: (
    type: string,
    listener: (event: { preventDefault: () => void }) => void,
  ) => void;
  sessionStorage: Pick<Storage, 'getItem' | 'setItem'>;
  reload: () => void;
  now: () => number;
};

/**
 * Reloads the app once when Vite cannot fetch a lazily imported chunk, so users
 * with a tab open across a deploy recover instead of facing a dead route.
 */
export function registerStaleChunkReload(
  target: StaleChunkReloadTarget = {
    addEventListener: (type, listener) =>
      window.addEventListener(type, listener as EventListener),
    sessionStorage: window.sessionStorage,
    reload: () => window.location.reload(),
    now: () => Date.now(),
  },
): void {
  target.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    if (shouldReloadForStaleChunk(target.sessionStorage, target.now())) {
      target.reload();
    }
  });
}
